import { ErrorHandler, Injectable, Injector, inject } from '@angular/core';
import { FrontendErrorReporterService } from './frontend-error-reporter.service';

/**
 * ErrorHandler toàn cục: giữ hành vi mặc định (console.error) và báo lỗi RUNTIME lên màn
 * Giám sát → Lỗi hệ thống (statusCode 0, source 'runtime').
 *
 * - Lấy reporter LƯỜI qua Injector — ErrorHandler dựng trước gần như mọi thứ, tiêm thẳng
 *   chuỗi Router/ReusableService/HttpClient dễ thành vòng DI.
 * - Không bao giờ ném: lỗi ném từ đây sẽ quay lại chính ErrorHandler.
 * - Bỏ qua lỗi mang 'status' dạng số: đó là lỗi HTTP chảy tới đây vì một subscribe thiếu
 *   nhánh error — interceptor đã quyết định báo hay không, báo lại là bản trùng.
 * - Chặn tái nhập: lỗi phát sinh TRONG lúc đang báo không được báo tiếp.
 * - E-133: sau một lần deploy FE, tab đang mở còn trỏ tên chunk cũ đã bị xoá ⇒ mọi điều hướng
 *   lazy ném "Failed to fetch dynamically imported module". Tải lại trang MỘT lần để lấy
 *   index.html mới. Chốt chống vòng lặp: sessionStorage giữ thông điệp lỗi đã tải lại; cùng
 *   thông điệp lần hai thì KHÔNG tải lại nữa (chunk hỏng thật, không phải chunk cũ).
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly injector = inject(Injector);
  private reporting = false;

  handleError(error: unknown): void {
    try {
      console.error(error);
    } catch {
      /* console có thể bị thay thế trong môi trường lạ — không được ném */
    }

    if (this.reporting) return;
    this.reporting = true;
    try {
      const err = unwrap(error);
      if (isHttpLike(err)) return;
      if (isChunkLoadError(err) && this.reloadOnceForStaleChunk(describe(err))) return;

      const reporter = this.injector.get(FrontendErrorReporterService, null);
      if (!reporter) return;
      const route = reporter.currentRoute() || '/';
      reporter.report({
        url: route,
        statusCode: 0,
        message: describe(err),
        stackTrace: err instanceof Error ? (err.stack ?? undefined) : undefined,
        source: 'runtime',
      });
    } catch {
      /* nuốt — xem doc-comment lớp */
    } finally {
      this.reporting = false;
    }
  }

  /** true khi đã ra lệnh tải lại; false khi chốt chặn (đã thử với cùng lỗi, hoặc storage hỏng). */
  private reloadOnceForStaleChunk(message: string): boolean {
    try {
      if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === message) return false;
      sessionStorage.setItem(CHUNK_RELOAD_KEY, message);
    } catch {
      // Không có storage thì không chốt được vòng lặp — thà báo lỗi còn hơn tải lại vô hạn.
      return false;
    }
    this.reloadPage();
    return true;
  }

  /** Tách riêng để spec thay được; jsdom không cho ghi đè window.location.reload. */
  protected reloadPage(): void {
    window.location.reload();
  }
}

export const CHUNK_RELOAD_KEY = 'app.chunkReloadAttempt';

const CHUNK_ERROR_PATTERNS = ['Failed to fetch dynamically imported module', 'Loading chunk', 'ChunkLoadError'];

function isChunkLoadError(err: unknown): boolean {
  const text =
    err instanceof Error ? `${err.name} ${err.message}` : typeof err === 'string' ? err : '';
  return CHUNK_ERROR_PATTERNS.some((p) => text.includes(p));
}

/** Promise bị từ chối bọc lỗi gốc trong 'rejection'; Angular cũ bọc trong 'ngOriginalError'. */
function unwrap(error: unknown): unknown {
  if (error && typeof error === 'object') {
    const e = error as { rejection?: unknown; ngOriginalError?: unknown };
    if (e.rejection !== undefined) return e.rejection;
    if (e.ngOriginalError !== undefined) return e.ngOriginalError;
  }
  return error;
}

function isHttpLike(err: unknown): boolean {
  return !!err && typeof err === 'object' && typeof (err as { status?: unknown }).status === 'number';
}

function describe(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  if (typeof err === 'string') return err;
  try {
    return JSON.stringify(err) ?? String(err);
  } catch {
    return String(err);
  }
}
